import { Type } from 'class-transformer';
import { TypeUtils } from '@utils';
import { CarModel } from '../car-model';
import { CarListBand } from '../car-list-band';
import { SimulationTask } from './simulation-task.class';
import { SimulationTaskStatus } from './simulation-task-status.enum';

const makeUnique = <T>(input: T[]): T[] => [...new Set(input)];

export class SimulationTasksGroupedByCarListBand {
	@Type(() => CarListBand)
	carListBand: CarListBand;

	@Type(() => SimulationTask)
	tasks: SimulationTask[];

	@Type(() => SimulationTask)
	subRows: SimulationTask[];

	get id() {
		return this.carListBand.id;
	}

	get conversionListTableName() {
		return this.carListBand.label;
	}

	get conversionListTableCurrentCar() {
		return makeUnique(this.tasks.map((task) => task?.fleetVehicle?.carModel?.brand).filter(Boolean)) as string[];
	}

	get conversionListTableVoltaageSuggests() {
		return makeUnique(this.tasks.map((task) => task?.proposedTaskVehicle?.carModel?.brand).filter(Boolean)) as string[];
	}

	get conversionListTableEvSelection() {
		return this.tasks
			.filter(({ fleetVehicle }) => fleetVehicle?.selectedCarModel)
			.reduce((acc, { fleetVehicle }) => {
				if (acc.find((model) => model.id === fleetVehicle.selectedCarModel.id)) {
					return acc;
				} else {
					return [...acc, fleetVehicle.selectedCarModel];
				}
			}, [] as CarModel[]);
	}

	get conversionListTableBudget() {
		return this.carListBand?.budgetRange || '';
	}

	get conversionListTableLeasing() {
		return this.tasks.reduce((total, { fleetVehicle }) => (fleetVehicle?.leasingPrice || 0) + total, 0);
	}

	static fromTasks(tasks: SimulationTask[], carListBands?: CarListBand[]): SimulationTasksGroupedByCarListBand[] {
		return tasks
			.filter((task) => task?.status === SimulationTaskStatus.FULFILLED)
			.reduce((mappedItems, current) => {
				let currentCarListBand = current?.fleetVehicle?.segment;
				if (!currentCarListBand) {
					currentCarListBand = carListBands?.find(({ id }) => current?.fleetVehicle?.segmentId === id);
				}

				if (currentCarListBand) {
					const findIndex = mappedItems.findIndex(({ carListBand }) => carListBand.id === currentCarListBand?.id);
					if (findIndex >= 0) {
						mappedItems[findIndex].tasks.push(current);
						mappedItems[findIndex].subRows.push(current);
					} else {
						mappedItems.push(
							TypeUtils.convert(SimulationTasksGroupedByCarListBand, {
								carListBand: currentCarListBand,
								tasks: [current],
								subRows: [current],
							})
						);
					}
				}
				return mappedItems;
			}, [] as SimulationTasksGroupedByCarListBand[]);
	}
}
